import React, { useState, useEffect, useCallback } from 'react';
import { supabase, insertRow, deleteRow } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import ClienteSelect from '../../components/ClienteSelect';
import { Upload, Search, Trash2, Download, FolderOpen, FileText, Image, File } from 'lucide-react';
import { fmtFecha } from '../../utils/fecha';
import { coincideBusqueda } from '../../utils/buscar';

const ENTIDADES = [
  { key:'cliente', label:'Cliente' },
  { key:'poliza', label:'Póliza' },
  { key:'reclamo', label:'Reclamo' },
];

function getIcon(tipo) {
  if (!tipo) return <File size={16} />;
  if (tipo.includes('pdf')) return <FileText size={16} style={{ color:'var(--text-danger)' }} />;
  if (tipo.includes('image')) return <Image size={16} style={{ color:'var(--sky)' }} />;
  return <File size={16} style={{ color:'var(--silver)' }} />;
}
function formatSize(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

export default function Archivos() {
  const { user, canEdit, agenteScope, esAgenteRestringido } = useApp();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [filterEntidad, setFilterEntidad] = useState('');
  const [uploading, setUploading] = useState(false);
  const [entidades, setEntidades] = useState([]);
  const [entidadTipo, setEntidadTipo] = useState('poliza');
  const [entidadId, setEntidadId] = useState('');

  const load = useCallback(async () => {
    if (esAgenteRestringido && !agenteScope) return;
    let q = supabase.from('archivos').select('*').order('creado_en', { ascending:false });
    if (filterEntidad) q = q.eq('entidad', filterEntidad);
    // Agente restringido: solo archivos de su cartera (sus pólizas / clientes / reclamos)
    if (esAgenteRestringido) {
      const pol = (agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]).join(',');
      const cli = (agenteScope?.clienteIds?.length ? agenteScope.clienteIds : [-1]).join(',');
      const rec = (agenteScope?.reclamoIds?.length ? agenteScope.reclamoIds : [-1]).join(',');
      q = q.or(`and(entidad.eq.poliza,entidad_id.in.(${pol})),and(entidad.eq.cliente,entidad_id.in.(${cli})),and(entidad.eq.reclamo,entidad_id.in.(${rec}))`);
    }
    let { data } = await q;
    // Búsqueda por palabras sueltas, sin acentos y en cualquier orden (igual que ClienteSelect).
    if (search) data = (data || []).filter(a => coincideBusqueda(a.nombre || '', search));
    setRows(data || []);
  }, [search, filterEntidad, esAgenteRestringido, agenteScope]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (esAgenteRestringido && !agenteScope) return;
    (async () => {
      let res;
      if (entidadTipo === 'poliza') {
        let q = supabase.from('polizas').select('id, numero').order('numero');
        if (esAgenteRestringido) q = q.in('id', agenteScope?.polizaIds?.length ? agenteScope.polizaIds : [-1]);
        res = await q;
      } else if (entidadTipo === 'cliente') {
        let q = supabase.from('clientes').select('id, nombre').order('nombre');
        if (esAgenteRestringido) q = q.in('id', agenteScope?.clienteIds?.length ? agenteScope.clienteIds : [-1]);
        res = await q;
      } else if (entidadTipo === 'reclamo') {
        let q = supabase.from('reclamos').select('id, numero').order('numero');
        if (esAgenteRestringido) q = q.in('id', agenteScope?.reclamoIds?.length ? agenteScope.reclamoIds : [-1]);
        res = await q;
      }
      setEntidades((res?.data || []).map(x => ({ id:x.id, label:x.numero || x.nombre })));
      setEntidadId('');
    })();
  }, [entidadTipo, esAgenteRestringido, agenteScope]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !entidadId) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} excede 5MB. No se subirá.`);
        continue;
      }
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (ev) => {
          try {
            await insertRow('archivos', {
              nombre:file.name, tipo:file.type, tamano:file.size,
              entidad:entidadTipo, entidad_id:parseInt(entidadId),
              datos:ev.target.result, subido_por:user?.id
            });
          } catch (err) {
            alert('Error subiendo ' + file.name);
          }
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    await load();
    setUploading(false);
    e.target.value = '';
  };

  const handleDownload = (row) => {
    if (!row.datos) return;
    const a = document.createElement('a');
    a.href = row.datos; a.download = row.nombre; a.click();
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar "${row.nombre}"?`)) return;
    try { await deleteRow('archivos', row.id); await load(); }
    catch (e) { alert('Error: ' + (e.message || '')); }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Archivos</div><div className="page-subtitle">{rows.length} documentos</div></div>
      </div>

      {canEdit('archivos') && (
        <div className="card" style={{ marginBottom:24 }}>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Subir Documento</div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
            <div className="form-group" style={{ minWidth:160 }}>
              <label className="form-label">Tipo de entidad</label>
              <select className="form-select" value={entidadTipo} onChange={e=>setEntidadTipo(e.target.value)}>{ENTIDADES.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}</select>
            </div>
            <div className="form-group" style={{ minWidth:200 }}>
              <label className="form-label">Seleccionar {ENTIDADES.find(e => e.key === entidadTipo)?.label}</label>
              {entidadTipo === 'cliente'
                ? <ClienteSelect value={entidadId} onChange={setEntidadId} width={260} />
                : <select className="form-select" value={entidadId} onChange={e=>setEntidadId(e.target.value)}><option value="">Seleccionar...</option>{entidades.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}</select>
              }
            </div>
            <div>
              <label className={`btn ${entidadId ? 'btn-primary' : 'btn-secondary'}`} style={{ cursor: entidadId ? 'pointer' : 'not-allowed', opacity: entidadId ? 1 : 0.5 }}>
                <Upload size={16} />{uploading ? 'Subiendo...' : 'Seleccionar archivos'}
                <input type="file" multiple style={{ display:'none' }} onChange={handleUpload} disabled={!entidadId || uploading} />
              </label>
            </div>
          </div>
          <div style={{ marginTop:8, fontSize:12, color:'var(--silver)' }}>Soporta PDF, imágenes, Word, Excel y más. Máximo 5MB por archivo.</div>
        </div>
      )}

      <div className="toolbar">
        <div className="search-bar" style={{ flex:1, maxWidth:400 }}>
          <Search size={16} style={{ color:'var(--silver)', flexShrink:0 }} />
          <input placeholder="Buscar archivo..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width:160 }} value={filterEntidad} onChange={e=>setFilterEntidad(e.target.value)}>
          <option value="">Todos los tipos</option>{ENTIDADES.map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
        </select>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr><th>Archivo</th><th>Tipo</th><th>Entidad</th><th>Tamaño</th><th>Fecha</th><th>Acciones</th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon"><FolderOpen size={48} /></div><div className="empty-state-title">Sin archivos</div></div></td></tr>
            ) : rows.map(r => (
              <tr key={r.id}>
                <td><div style={{ display:'flex', alignItems:'center', gap:8 }}>{getIcon(r.tipo)}<span style={{ fontWeight:600 }}>{r.nombre}</span></div></td>
                <td className="text-silver" style={{ fontSize:12 }}>{r.tipo || '—'}</td>
                <td><span className="badge badge-info">{r.entidad} #{r.entidad_id}</span></td>
                <td className="text-silver">{formatSize(r.tamano)}</td>
                <td className="text-silver">{fmtFecha(r.creado_en)}</td>
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-sm btn-secondary" onClick={()=>handleDownload(r)}><Download size={13} /> Descargar</button>
                    {canEdit('archivos') && <button className="btn btn-icon btn-danger btn-sm" onClick={()=>handleDelete(r)}><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
